import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ next: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 8) { setError("Your new password must be at least 8 characters."); return; }
    if (form.next !== form.confirm) { setError("The passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-6 py-16" style={{ background: "#FDFBF7" }} data-testid="reset-password-page">
      <div className="w-full max-w-sm fade-up">
        <Link to="/" className="flex items-center gap-2 mb-10"><Heart size={20} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-3xl tracking-tight">Evermore</span></Link>
        <h1 className="font-serif text-4xl tracking-tight mb-2">Choose a new password</h1>
        {!token ? (
          <p className="text-sm text-[#C67A7A] mt-4" data-testid="reset-no-token">This reset link is missing its token. Please use the link from your email, or request a new one from the sign-in page.</p>
        ) : done ? (
          <div className="mt-6">
            <div className="rounded-lg bg-[#8C9A86]/12 text-[#5f6b59] px-5 py-4 text-sm" data-testid="reset-success">Your password has been updated. You can now sign in with your new password.</div>
            <button onClick={() => navigate("/login")} data-testid="go-to-login-btn" className="mt-5 w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm font-medium hover:bg-[#8C9A86] transition-colors">Go to sign in</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-6">
            <p className="text-stone-500 text-sm">Enter a new password of at least 8 characters.</p>
            <Field label="New password" value={form.next} onChange={(v) => setForm({ ...form, next: v })} testid="reset-new-password" />
            <Field label="Confirm new password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} testid="reset-confirm-password" />
            {error && <p data-testid="reset-error" className="text-sm text-[#C67A7A]">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm font-medium hover:bg-[#8C9A86] transition-colors disabled:opacity-50">{loading ? "Updating…" : "Update password"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input type="password" value={value} required data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
